/**
 * Centralized token utilities for consistent JWT handling across the app
 */

/**
 * JWTs use base64URL encoding (RFC 4648 §5: '-'/'_' instead of '+'/'/', no padding),
 * not plain base64 — atob() only understands plain base64 and throws
 * InvalidCharacterError on any payload that happens to contain '-' or '_'. This
 * converts the segment to standard base64 (restoring padding) before decoding.
 */
export const decodeBase64Url = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return atob(padded);
};

export interface DecodedToken {
  business?: {
    id: string;
    name: string;
  };
  subscription?: {
    services?: {
      wabu?: {
        number_of_whatsapp_numbers?: number;
        metadata?: {
          whatsapp_phone?: Array<{
            whatsapp_phone_number: string;
            whatsapp_business_account_id: string;
            whatsapp_phone_number_id: string;
            status: string;
            registered_at: string;
            is_default?: boolean;
          }>;
        };
      };
      fbm?: {
        number_of_facebook_pages?: number;
        metadata?: {
          facebook_pages?: Array<{
            page_id: string;
            page_name: string;
            page_access_token: string;
            status: string;
            registered_at: string;
            is_default?: boolean;
          }>;
        };
      };
      x_twitter?: {
        number_of_x_accounts?: number;
        send_dm?: boolean;
        read_dm?: boolean;
        read_mentions?: boolean;
        metadata?: {
          x_accounts?: Array<{
            x_user_id: string;
            x_username: string;
            display_name: string;
            profile_image_url?: string;
            oauth2_expires_at: string;
            webhook_subscribed: boolean;
            status: string;
            registered_at: string;
            is_default?: boolean;
          }>;
        };
      };
      phone?: {
        provider: string;
        providerConfig?: Record<string, any>;
        callerNumber?: string;
        status: 'active' | 'inactive' | 'invalid';
        connectedAt: string;
        updatedAt?: string;
      };
      visits?: {
        status: 'active' | 'inactive';
        connectedAt: string;
        updatedAt?: string;
        [key: string]: any;
      };
      ai?: {
        status: 'active' | 'inactive'; modelKey?: string; model?: string; provider?: string;
        lastTestedAt?: string; connectedAt?: string;
      };
    };
  };
  [key: string]: any;
}

/**
 * Decode JWT token and return parsed payload
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export const decodeJWT = (token: string): DecodedToken | null => {
  try {
    if (!token || typeof token !== 'string') {
      console.log('❌ [TokenUtils] Invalid token format:', { token, type: typeof token });
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      console.log('❌ [TokenUtils] Invalid JWT structure:', { parts: parts.length });
      return null;
    }

    const payload = parts[1];
    const decoded = JSON.parse(decodeBase64Url(payload));
    
    console.log('✅ [TokenUtils] Token decoded successfully:', {
      hasBusiness: !!decoded.business,
      hasSubscription: !!decoded.subscription,
      hasWabuService: !!decoded.subscription?.services?.wabu,
      wabuMetadata: decoded.subscription?.services?.wabu?.metadata,
      wabuPhoneCount: decoded.subscription?.services?.wabu?.metadata?.whatsapp_phone?.length || 0
    });
    
    return decoded;
  } catch (error) {
    console.error('❌ [TokenUtils] Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if WhatsApp service is configured in token
 * @param token - JWT token string
 * @returns Object with configuration status
 */
export const checkWhatsAppConfiguration = (token: string) => {
  const decoded = decodeJWT(token);
  if (!decoded) {
    return {
      isConfigured: false,
      hasMetadata: false,
      phoneCount: 0,
      verifiedPhones: 0,
      pendingPhones: 0
    };
  }

  const wabuService = decoded.subscription?.services?.wabu;
  const metadata = wabuService?.metadata;
  const phones = metadata?.whatsapp_phone || [];

  const verifiedPhones = phones.filter(p => p.status === 'verified' || p.status === 'active').length;
  const pendingPhones = phones.filter(p => p.status === 'pending').length;

  return {
    isConfigured: !!wabuService,
    hasMetadata: !!metadata,
    phoneCount: phones.length,
    verifiedPhones,
    pendingPhones,
    phones
  };
};

/**
 * Check if Facebook Messenger service is configured in token
 * @param token - JWT token string
 * @returns Object with configuration status
 */
export const checkFacebookMessengerConfiguration = (token: string) => {
  const decoded = decodeJWT(token);
  if (!decoded) {
    return {
      isConfigured: false,
      hasMetadata: false,
      pageCount: 0,
      verifiedPages: 0,
      pendingPages: 0
    };
  }

  const fbmService = decoded.subscription?.services?.fbm;
  const metadata = fbmService?.metadata;
  const pages = metadata?.facebook_pages || [];

  const verifiedPages = pages.filter(p => p.status === 'verified' || p.status === 'active').length;
  const pendingPages = pages.filter(p => p.status === 'pending').length;

  return {
    isConfigured: !!fbmService,
    hasMetadata: !!metadata,
    pageCount: pages.length,
    verifiedPages,
    pendingPages,
    pages
  };
};

/**
 * Get X (Twitter) subscription service object from JWT token
 * @param token - JWT token string
 * @returns x_twitter service object or null if unavailable
 */
export const getXTwitterSubscriptionObject = (token: string) => {
  const decoded = decodeJWT(token);
  return decoded?.subscription?.services?.x_twitter || null;
};

/**
 * Check if X (Twitter) service is configured in token
 * @param token - JWT token string
 * @returns Object with configuration status
 */
export const checkXTwitterConfiguration = (token: string) => {
  const xService = getXTwitterSubscriptionObject(token);
  const metadata = xService?.metadata;
  const accounts = metadata?.x_accounts || [];
  const now = Date.now();

  const activeAccounts = accounts.filter((acc) => {
    const isActiveStatus = acc.status === 'active' || acc.status === 'verified';
    const expiresAt = acc.oauth2_expires_at ? new Date(acc.oauth2_expires_at).getTime() : 0;
    const notExpired = expiresAt > now;
    return isActiveStatus && notExpired;
  }).length;

  const expiredAccounts = accounts.filter((acc) => {
    const expiresAt = acc.oauth2_expires_at ? new Date(acc.oauth2_expires_at).getTime() : 0;
    return !expiresAt || expiresAt <= now;
  }).length;

  return {
    isConfigured: !!xService,
    hasMetadata: !!metadata,
    accountCount: accounts.length,
    activeAccounts,
    expiredAccounts,
    accounts,
  };
};

/**
 * Get phone (voice calling) subscription service object from JWT token
 * @param token - JWT token string
 * @returns phone service object or null if unavailable
 */
export const getPhoneSubscriptionObject = (token: string) => {
  const decoded = decodeJWT(token);
  return decoded?.subscription?.services?.phone || null;
};

/**
 * Check if the phone channel is configured in token.
 * Unlike WhatsApp/Facebook/X, the phone channel supports a single connected
 * account (via whichever provider is configured, e.g. InnoCalls), not an array.
 * @param token - JWT token string
 * @returns Object with configuration status
 */
export const checkPhoneConfiguration = (token: string) => {
  const phoneService = getPhoneSubscriptionObject(token);

  return {
    isConfigured: !!phoneService,
    isActive: phoneService?.status === 'active',
    provider: phoneService?.provider || null,
    callerNumber: phoneService?.callerNumber || null,
  };
};

export const getVisitsSubscriptionObject = (token: string) => {
  const decoded = decodeJWT(token);
  return decoded?.subscription?.services?.visits || null;
};

export const checkVisitsConfiguration = (token: string) => {
  const visitsService = getVisitsSubscriptionObject(token);
  return {
    isConfigured: !!visitsService,
    isActive: visitsService?.status === 'active',
  };
};
